import type { FlightsClassTypes } from 'constants/flight'
import { useContext, useMemo } from 'react'
import { getFlightFareFamilies } from 'api/flights'
import GeoContext from 'contexts/geoContext'
import { countOccupantsForFlights, mergeOccupants } from 'lib/offer/occupancyUtils'
import { useQuery } from '@tanstack/react-query'
import { EmptyArray } from 'lib/array/arrayUtils'
import { EmptyObject } from 'lib/object/objectUtils'
import { getFeaturesByFareFamily } from 'lib/flights/flightUtils'
import useAllCharterFlightJourneys from './useAllCharterFlightJourneys'
import { extractDepartingAndReturningFaresForClass } from './useCharterFlightJourneysForHotelDates'
import { charterCalendarDayKey } from 'lib/maldivesCharter/charterCalendarDayKey'
import { useAppSelector } from 'hooks/reduxHooks'

/**
 * Fare upsell + feature rows for a single charter cabin on specific trip dates (Maldives calendar fare cards).
 * Matches standalone flights fare-family fetch used after journey search.
 */
export default function useCharterDateFareFlightFareFamilies({
  charterBundleRoute,
  checkIn,
  checkOut,
  occupancies = EmptyArray,
  fareClass,
}: {
  charterBundleRoute?: App.CharterBundleRoute
  checkIn: string
  checkOut: string
  occupancies: Array<App.Occupants>
  fareClass: FlightsClassTypes
}): {
  departingFareFamily: App.FlightFareFamily | undefined
  features: Array<App.FareFamilyFeature> | undefined
  fetchingFareFeatures: boolean
  fareFeaturesError: boolean
  /** TanStack Query succeeded (upsell payload may still have no matching cabin). */
  fareFeaturesFetchSuccess: boolean
  /** `getFlightFareFamilies` can run for this row (search id + both legs matched). */
  fareFeaturesFetchEnabled: boolean
  /** Matched outbound journey for the row’s check-in (used for loyalty earn in comparison cells). */
  departingJourney: App.JourneyV2 | undefined
  /** Matched return journey for the row’s check-out. */
  returningJourney: App.JourneyV2 | undefined
} {
  const { currentRegionCode } = useContext(GeoContext)
  const email = useAppSelector(state => state.auth.account.email)

  // Do not gate on hotel/bundle loading: calendar prices can show from flight Redux while
  // `useCalendarItemData` is still fetching; fare upsells must still run so “View details” can appear.
  const flightResults = useAllCharterFlightJourneys({
    charterBundleRoute,
    fareClass,
    occupancies,
    disabled: !charterBundleRoute,
  })

  const providerSearchId = flightResults?.searchId

  const checkInKey = charterCalendarDayKey(checkIn)
  const checkOutKey = charterCalendarDayKey(checkOut)

  const departingLegFares = flightResults?.fares?.[0]
  const returningLegFares = flightResults?.fares?.[1]

  const departingJourney = checkInKey && departingLegFares
    ? departingLegFares.find(journey => {
        const arrivalDate = journey.flightGroup.flights.at(-1)?.arrivalDate
        const arrivalKey = charterCalendarDayKey(arrivalDate)
        return arrivalKey !== undefined && arrivalKey === checkInKey
      })
    : undefined

  const returningJourney = checkOutKey && returningLegFares
    ? returningLegFares.find(journey => {
        const departingDate = journey.flightGroup.flights.at(0)?.departingDate
        const departingKey = charterCalendarDayKey(departingDate)
        return departingKey !== undefined && departingKey === checkOutKey
      })
    : undefined

  const { data, isFetching, isError, isSuccess } = useQuery<{
    departingFareFamily?: App.FlightFareFamily
  }>({
    queryKey: [
      'charterDateFareFlightFareFamilies',
      fareClass,
      departingJourney?.id,
      returningJourney?.id,
      occupancies,
      currentRegionCode,
      email,
    ],
    queryFn: async() => {
      if (!providerSearchId || !departingJourney || !returningJourney) {
        return EmptyObject
      }

      const flightOccupancies = countOccupantsForFlights(occupancies)
      const fareFamilies = await getFlightFareFamilies({
        searchId: providerSearchId,
        sessionId: '',
        origin: departingJourney.flightGroup.flights[0].departureAirport,
        destination: returningJourney.flightGroup.flights[0].departureAirport,
        occupancies: mergeOccupants([flightOccupancies]),
        region: currentRegionCode,
        isStandalone: false,
        selectedFareIds: [departingJourney.id, returningJourney.id],
        email,
      })

      const [departingFF] = extractDepartingAndReturningFaresForClass(fareFamilies, fareClass)

      return { departingFareFamily: departingFF }
    },
    enabled: !!(providerSearchId && departingJourney && returningJourney && charterBundleRoute),
  })

  const departingFareFamily = data?.departingFareFamily

  const features = useMemo(() => {
    if (!departingFareFamily) {
      return undefined
    }
    const byFamily = getFeaturesByFareFamily([departingFareFamily])
    return byFamily.get(departingFareFamily.id)
  }, [departingFareFamily])

  const queryEnabled = !!(
    providerSearchId
    && departingJourney
    && returningJourney
    && charterBundleRoute
  )

  const fetchingFareFeatures = isFetching || (queryEnabled && data === undefined && !isError)

  return {
    departingFareFamily,
    features,
    fetchingFareFeatures,
    fareFeaturesError: isError,
    fareFeaturesFetchSuccess: isSuccess,
    fareFeaturesFetchEnabled: queryEnabled,
    departingJourney,
    returningJourney,
  }
}
